'use client';
import { gql } from '@apollo/client';
import { ReportSectionState } from '@mnd-frontend/graphql-schema';
import { toBoolean } from '@mnd-frontend/ts';
import styled from 'styled-components';
import { usePollingVisibilityHighlightsQuery } from '../../../../../generated/hooks';
import type { Report2VisibilityHighlightsFragment } from '../../../../../generated/types';
import {
  useReportDynamicData,
  useReportEditable,
  useReportStaticData,
  useReportTranslation,
} from '../../../hooks';
import { Quotation, Section, SectionHeader, SkeletonQuotation } from '../../common';
import { AddButton } from './AddButton';

export const REPORT2_VISIBILITY_HIGHLIGHTS_FRAGMENT = gql`
  fragment ReportQuotation2 on ReportCoverageInterface {
    id
    sortIndex
    iconUrl
    iconUrlNoFallback
    imageUrl
    publishedAt
    source
    title
    url
    featured
    hidden
    description
    reach
    engagement
    prValue
    prValueCurrency
    sentiment
    embed
    audioUrl
    audioHitlines {
      id
      startPos
      endPos
      hitline
    }
  }

  fragment Report2VisibilityHighlights on ReportVisibilityHighlights {
    id
    title
    state
    activeImportJob {
      id
      state
      leftToProcess
    }
    quotations {
      totalCount
      edges {
        cursor
        node {
          id
          ...ReportQuotation2
        }
      }
    }
  }

  query PollingVisibilityHighlights($reportId: ID!) {
    report2(id: $reportId) {
      id
      sections2 {
        id
        ... on ReportVisibilitySection {
          id
          subSections {
            id
            ... on ReportVisibilityHighlights {
              ...Report2VisibilityHighlights
            }
          }
        }
      }
    }
  }
`;

const ReachHighlightsList = styled.div`
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  grid-gap: 1rem;
`;

const VisibilityHighlights = ({ section }: { section: Report2VisibilityHighlightsFragment }) => {
  const { brandColor } = useReportDynamicData();
  const { id } = useReportStaticData();
  const { editable, openEditHighlights } = useReportEditable();
  const { t } = useReportTranslation();
  const edges = section?.quotations?.edges || [];
  const quotations = edges?.map(edge => edge?.node).filter(toBoolean) || [];
  const activeImportJob = section?.activeImportJob;

  usePollingVisibilityHighlightsQuery({
    variables: { reportId: id },
    pollInterval: activeImportJob ? 6000 : 0,
    skip: !activeImportJob,
  });

  if (section.state !== ReportSectionState.Done) {
    return (
      <Section>
        <SectionHeader
          as="h3"
          color="GREY_900"
          variant="reportHeadlineLarge"
          section={section}
          isSubsection
        />
        <ReachHighlightsList>
          <SkeletonQuotation />
          <SkeletonQuotation />
          <SkeletonQuotation />
        </ReachHighlightsList>
      </Section>
    );
  }

  return (
    <Section>
      <SectionHeader
        as="h3"
        color="GREY_900"
        variant="reportHeadlineLarge"
        section={section}
        isSubsection
      />
      <ReachHighlightsList>
        {quotations.map(q => (
          <Quotation
            key={q.id}
            quotation={q}
            showPrValue={true}
            showSentiment={true}
            showReach={true}
            showEngagement={false}
            brandColor={brandColor}
            sentiment={q.sentiment ?? undefined}
          />
        ))}
        {activeImportJob
          ? Array.from({ length: activeImportJob.leftToProcess }).map((_, i) => (
              <SkeletonQuotation key={i} />
            ))
          : null}
      </ReachHighlightsList>
      {editable && openEditHighlights ? (
        <AddButton
          disabled={!!activeImportJob}
          testId="report-visibility-highlights-manage"
          onClick={() => openEditHighlights()}
        >
          {t('reports_manage_reach_highlights')}
        </AddButton>
      ) : null}
    </Section>
  );
};

export default VisibilityHighlights;
